import React, { useEffect, useRef } from 'react'

// Pointer light for the navy heroes: a soft glow trails the cursor and
// shifts from blue to teal across the section. Draws only while visible.

const lerp = (a, b, t) => a + (b - a) * t

const Fluid = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas && canvas.parentElement
    if (!host) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const ctx = canvas.getContext('2d')
    const glow = { x: 0, y: 0, tx: 0, ty: 0, a: 0, ta: 0 }
    let raf = 0
    let w = 0
    let h = 0

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = host.clientWidth
      h = host.clientHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const frame = () => {
      ctx.clearRect(0, 0, w, h)
      ctx.globalCompositeOperation = 'lighter'

      glow.x = lerp(glow.x, glow.tx, 0.08)
      glow.y = lerp(glow.y, glow.ty, 0.08)
      glow.a = lerp(glow.a, glow.ta, 0.06)

      if (glow.a > 0.005) {
        // Hue drifts from the site blue (≈220) to teal (≈175) left to right.
        const hue = lerp(222, 175, glow.x / (w || 1))
        const radius = Math.max(260, Math.min(w, h) * 0.55)
        const g = ctx.createRadialGradient(glow.x, glow.y, 0, glow.x, glow.y, radius)
        g.addColorStop(0, `hsla(${hue}, 95%, 62%, ${0.32 * glow.a})`)
        g.addColorStop(0.45, `hsla(${hue + 15}, 90%, 55%, ${0.12 * glow.a})`)
        g.addColorStop(1, 'hsla(220, 90%, 50%, 0)')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, w, h)
      }

      const settled =
        Math.abs(glow.a - glow.ta) < 0.005 &&
        Math.abs(glow.x - glow.tx) < 0.5 &&
        Math.abs(glow.y - glow.ty) < 0.5
      if (!settled) {
        raf = requestAnimationFrame(frame)
      } else {
        raf = 0
        if (glow.ta === 0) ctx.clearRect(0, 0, w, h)
      }
    }

    const wake = () => {
      if (!raf) raf = requestAnimationFrame(frame)
    }

    const onMove = (e) => {
      const rect = host.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      if (glow.ta === 0) {
        glow.x = x
        glow.y = y
      }
      glow.tx = x
      glow.ty = y
      glow.ta = 1
      wake()
    }

    const onLeave = () => {
      glow.ta = 0
      wake()
    }

    resize()
    window.addEventListener('resize', resize)
    host.addEventListener('pointermove', onMove)
    host.addEventListener('pointerleave', onLeave)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerleave', onLeave)
    }
  }, [])

  return <canvas className='fluid' ref={canvasRef} aria-hidden='true' />
}

export default Fluid
